<template>
  <div class="query-flow-wrapper relative">
    <!-- Regular View -->
    <div v-if="!isFullScreen" class="graph-container-wrapper">
      <!-- Full Screen Toggle Button -->
      <button 
        @click="toggleFullScreen" 
        class="absolute top-4 right-4 z-10 bg-white p-2 rounded-lg shadow-md hover:shadow-lg transition-all"
      >
        <svg xmlns="http://www.w3.org/2000/svg" 
             class="h-6 w-6 text-gray-600"
             fill="none" 
             viewBox="0 0 24 24" 
             stroke="currentColor">
          <path 
            stroke-linecap="round" 
            stroke-linejoin="round" 
            stroke-width="2" 
            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
        </svg>
      </button>

      <div v-if="!isValidQuery" class="flex items-center justify-center h-full bg-gray-50 rounded-lg">
        <p class="text-gray-500">No valid query data available</p>
      </div>
      
      <div class="graph-scroll-container" v-else>
        <VueFlow
          v-model="nodes"
          :default-viewport="{ zoom: 1.5 }"
          :min-zoom="0.2"
          :max-zoom="4"
          :fit-view-on-init="true"
          :node-dimensions-change="true"
          :elevation="30"
          :style="graphStyle"
          class="vue-flow-graph"
          :nodes="nodes"
          :edges="edges"
          :enable-pan-over-scroll="true"
          :enable-zoom-over-scroll="true"
        >
          <!-- Add Zoom Controls -->
          <div class="viewport-controls absolute top-4 left-4 z-20 bg-white rounded-lg shadow-md p-2 flex flex-col gap-2">
            <button 
              @click="zoomIn"
              class="zoom-btn hover:bg-gray-100 rounded p-2 transition-colors"
              title="Zoom In"
            >
              <span class="text-xl font-semibold text-gray-700">+</span>
            </button>
            <div class="h-px bg-gray-200"></div>
            <button 
              @click="zoomOut"
              class="zoom-btn hover:bg-gray-100 rounded p-2 transition-colors"
              title="Zoom Out"
            >
              <span class="text-xl font-semibold text-gray-700">−</span>
            </button>
          </div>

          <template #node-custom="nodeProps">
            <div
              :class="[
                'custom-node',
                nodeProps.data.isCurrent ? 'current' : 'related',
                'transform-gpu'
              ]"
              @click="handleNodeClick(nodeProps.id)"
            >
              <div class="node-content">
                {{ nodeProps.data.label }}
              </div>
            </div>
          </template>
          <Background pattern-color="#aaa" :gap="12" />
          <Controls />
          <Panel position="top-right" class="mt-16">
            <button @click="onResetView" class="reset-view-btn">
              Reset View
            </button>
          </Panel>
        </VueFlow>
      </div>
    </div>

    <!-- Fullscreen Modal -->
    <div v-if="isFullScreen" 
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
         @click.self="toggleFullScreen">
      <div class="relative w-[95vw] h-[90vh] bg-white rounded-xl p-6 shadow-2xl">
        <!-- Close button -->
        <button 
          @click="toggleFullScreen"
          class="absolute top-4 right-4 z-10 bg-white p-2 rounded-lg shadow-md hover:shadow-lg transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" 
               class="h-6 w-6 text-[#157813]"
               fill="none" 
               viewBox="0 0 24 24" 
               stroke="currentColor">
            <path 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              stroke-width="2" 
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Fullscreen Graph with Scroll -->
        <div class="graph-scroll-container h-full">
          <VueFlow
            v-model="nodes"
            :default-viewport="{ zoom: 1.5 }"
            :min-zoom="0.2"
            :max-zoom="4"
            :fit-view-on-init="true"
            :node-dimensions-change="true"
            :elevation="30"
            class="vue-flow-graph h-full"
            :nodes="nodes"
            :edges="edges"
            :enable-pan-over-scroll="true"
            :enable-zoom-over-scroll="true"
          >
            <!-- Add Zoom Controls -->
            <div class="viewport-controls absolute top-4 left-4 z-20 bg-white rounded-lg shadow-md p-2 flex flex-col gap-2">
              <button 
                @click="zoomIn"
                class="zoom-btn hover:bg-gray-100 rounded p-2 transition-colors"
                title="Zoom In"
              >
                <span class="text-xl font-semibold text-gray-700">+</span>
              </button>
              <div class="h-px bg-gray-200"></div>
              <button 
                @click="zoomOut"
                class="zoom-btn hover:bg-gray-100 rounded p-2 transition-colors"
                title="Zoom Out"
              >
                <span class="text-xl font-semibold text-gray-700">−</span>
              </button>
            </div>

            <template #node-custom="nodeProps">
              <div
                :class="[
                  'custom-node',
                  nodeProps.data.isCurrent ? 'current' : 'related',
                  'transform-gpu'
                ]"
                @click="handleNodeClick(nodeProps.id)"
              >
                <div class="node-content">
                  {{ nodeProps.data.label }}
                </div>
              </div>
            </template>
            <Background pattern-color="#aaa" :gap="12" />
            <Controls />
            <Panel position="top-right" class="mt-16">
              <button @click="onResetView" class="reset-view-btn">
                Reset View
              </button>
            </Panel>
          </VueFlow>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import type { Edge, Node } from '@vue-flow/core'
import { Panel, useVueFlow, VueFlow } from '@vue-flow/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

// Import Vue Flow styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'

interface QueryNode {
  id: string;
  parentComponent: string;
}

const props = defineProps<{
  queries: QueryNode[];
  currentQueryId: string;
}>();

const emit = defineEmits<{
  (e: 'node-click', id: string): void;
}>();

const { onPaneReady, fitView, setCenter, setViewport, getViewport } = useVueFlow()
const router = useRouter()
const isFullScreen = ref(false)
const horizontalSpacing = ref(400)
const verticalSpacing = ref(250)

const nodes = ref<Node[]>([])
const edges = ref<Edge[]>([])

const isValidQuery = computed(() => {
  return props.currentQueryId && props.queries.some(q => q.id === props.currentQueryId);
});

const graphStyle = computed(() => ({
  height: '500px',
  width: '100%',
  minWidth: '400px'
}))

function handleNodeClick(queryId: string) {
  if (!queryId) return;
  
  // First zoom out to show the entire graph
  fitView({ duration: 400, padding: 0.5 });
  
  // After zooming out, wait a bit then navigate
  setTimeout(() => {
    isFullScreen.value = false;
    router.push(`/query/${queryId}`);
  }, 500);
}

const createElements = () => {
  if (!isValidQuery.value) {
    nodes.value = []
    edges.value = []
    return
  }

  const newNodes: Node[] = []
  const newEdges: Edge[] = []
  const processedNodes = new Set()

  // Process nodes starting from current query
  const processNode = (nodeId: string, x: number, y: number, level: number = 0) => {
    if (!nodeId || processedNodes.has(nodeId)) return
    processedNodes.add(nodeId)

    const node = props.queries.find(q => q.id === nodeId)
    if (!node) return

    // Add node with animation class for new nodes
    newNodes.push({
      id: nodeId,
      type: 'custom',
      position: { x, y },
      data: {
        label: nodeId,
        isCurrent: nodeId === props.currentQueryId,
        type: nodeId === props.currentQueryId ? 'current' : 'related',
        level,
        isNew: true // Add this flag for animation
      },
    })

    // Process parent with increased spacing for better visibility
    if (node.parentComponent) {
      const edgeId = `${node.parentComponent}-${nodeId}`
      newEdges.push({
        id: edgeId,
        source: node.parentComponent,
        target: nodeId,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#15803d', strokeWidth: 2 },
      })

      if (!processedNodes.has(node.parentComponent)) {
        processNode(
          node.parentComponent,
          x,
          y - verticalSpacing.value,
          level - 1
        )
      }
    }

    // Process children with wider spacing
    const children = props.queries.filter(q => q.parentComponent === nodeId)
    const childCount = children.length
    
    children.forEach((child, index) => {
      const offset = (index - (childCount - 1) / 2) * horizontalSpacing.value
      const childX = x + offset
      const childY = y + verticalSpacing.value
      
      processNode(child.id, childX, childY, level + 1)
    })
  }

  // Start processing from current query
  processNode(props.currentQueryId, 0, 0, 0)

  nodes.value = newNodes
  edges.value = newEdges

  // Ensure the graph is properly centered and zoomed
  setTimeout(() => {
    fitView({ 
      duration: 800,
      padding: 0.2,
      includeHiddenNodes: true,
    });
  }, 100);
}

// Watch for changes in currentQueryId or queries
watch([() => props.currentQueryId, () => props.queries], () => {
  createElements()
}, { immediate: true })

// Add a watch for currentQueryId changes
watch(() => props.currentQueryId, (newId, oldId) => {
  if (newId && oldId && newId !== oldId) {
    // When query changes, ensure we show the full graph
    setTimeout(() => {
      fitView({ 
        duration: 800,
        padding: 0.2,
        includeHiddenNodes: true,
      });
    }, 100);
  }
}, { immediate: true });

onMounted(() => {
  if (isValidQuery.value) {
    createElements()
  }
})

onPaneReady(({ fitView }) => {
  if (isValidQuery.value) {
    fitView()
  }
})

const onResetView = () => {
  if (isValidQuery.value) {
    fitView()
  }
}

function toggleFullScreen() {
  isFullScreen.value = !isFullScreen.value
  // Trigger a re-render of the graph
  setTimeout(() => {
    if (isValidQuery.value) {
      fitView()
    }
  }, 100)
}

// Add zoom control functions
const zoomIn = () => {
  const viewport = getViewport()
  const newZoom = Math.min(viewport.zoom * 1.2, 4)
  setViewport({ 
    x: viewport.x, 
    y: viewport.y, 
    zoom: newZoom 
  }, { duration: 300 })
}

const zoomOut = () => {
  const viewport = getViewport()
  const newZoom = Math.max(viewport.zoom / 1.2, 0.2)
  setViewport({ 
    x: viewport.x, 
    y: viewport.y, 
    zoom: newZoom 
  }, { duration: 300 })
}
</script>

<style>
.vue-flow-graph {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 20px;
  height: 500px;
}

.custom-node {
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  color: #fff;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 150px;
  position: relative;
  background: #475569;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.custom-node.current {
  background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
  box-shadow: 
    0 12px 20px -5px rgba(21, 128, 61, 0.4),
    0 8px 12px -4px rgba(21, 128, 61, 0.3);
  transform: translateY(-2px);
}

.custom-node.related {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
}

.custom-node:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.custom-node::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 0.875rem;
  padding: 2px;
  background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.node-content {
  position: relative;
  z-index: 2;
}

/* Edge styles */
.vue-flow__edge-path {
  stroke: #15803d;
  stroke-width: 2;
}

.vue-flow__edge.animated .vue-flow__edge-path {
  stroke: #15803d;
  stroke-width: 2;
  animation: flowEdge 1s infinite linear;
}

@keyframes flowEdge {
  from {
    stroke-dashoffset: 10;
  }
  to {
    stroke-dashoffset: 0;
  }
}

/* Controls styles */
.vue-flow__controls {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: white;
}

.vue-flow__controls button {
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
  padding: 4px;
  border-radius: 4px;
}

.vue-flow__controls button:hover {
  background: #f8fafc;
}

/* Reset view button */
.reset-view-btn {
  background-color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #334155;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.reset-view-btn:hover {
  background-color: #f8fafc;
  border-color: #94a3b8;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Add styles for the error state */
.query-flow-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.query-flow-wrapper.fixed {
  background: white;
  padding: 1rem;
}

.query-flow-wrapper p {
  font-size: 14px;
  text-align: center;
}

/* Animation for modal */
@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-content {
  animation: modalFadeIn 0.3s ease-out;
}

/* Add animation for new nodes */
@keyframes nodeAppear {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.custom-node[data-new="true"] {
  animation: nodeAppear 0.5s ease-out forwards;
}

/* Improve transition smoothness */
.vue-flow__node {
  transition: transform 0.5s ease-out, opacity 0.5s ease-out;
}

.vue-flow__edge {
  transition: opacity 0.5s ease-out;
}

/* Add these new styles for scrolling */
.graph-container-wrapper {
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 12px;
  overflow: hidden;
}

.graph-scroll-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  position: relative;
}

/* Customize scrollbar appearance */
.graph-scroll-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.graph-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.graph-scroll-container::-webkit-scrollbar-thumb {
  background: #157813;
  border-radius: 4px;
}

.graph-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #0f5c0e;
}

.graph-scroll-container::-webkit-scrollbar-corner {
  background: #f1f1f1;
}

/* Ensure the graph container fills the scroll container */
.vue-flow-graph {
  min-height: 100%;
  min-width: 100%;
}

/* Improve node visibility */
.custom-node {
  z-index: 5;
}

/* Add smooth scrolling */
.graph-scroll-container {
  scroll-behavior: smooth;
}

/* Remove minimap styles */
.vue-flow__minimap {
  display: none;
}

/* Add styles for zoom controls */
.viewport-controls {
  user-select: none;
}

.zoom-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.zoom-btn:hover {
  background-color: #f3f4f6;
}

.zoom-btn:active {
  transform: scale(0.95);
}

.zoom-btn span {
  line-height: 1;
}

/* Ensure controls are visible in fullscreen mode */
.vue-flow__viewport .viewport-controls {
  position: fixed;
}
</style>